import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  Check,
  Loader2,
  Lock,
  KeyRound,
  ShieldCheck,
} from "lucide-react";
import { setAdminToken } from "../lib/adminAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Brand-new admin sets their password and scans the TOTP QR. The token
 * comes from the invite email link. Once accepted the JWT is stored and
 * the user is dropped into the dashboard.
 */
export default function AdminAcceptInvitePage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [totp, setTotp] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("This invite link is missing its token. Ask the person who invited you to send a fresh link.");
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/auth/invite/${token}`);
        if (!cancelled) {
          setInvite(data);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          const detail =
            err?.response?.data?.detail || "This invite is invalid or has expired.";
          setError(detail);
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 12) {
      toast.error("Password must be at least 12 characters.");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/admin/auth/accept-invite`, {
        token,
        password,
        totp_code: totp.replace(/\s+/g, ""),
      });
      setAdminToken(data.token);
      toast.success("Account ready. Welcome!");
      navigate("/admin/dashboard");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Couldn't accept the invite.";
      toast.error(detail);
      setSubmitting(false);
    }
  };

  if (loading) {
    return <CenteredState>
      <Loader2 size={28} className="animate-spin text-[var(--lime)]" />
    </CenteredState>;
  }

  if (error || !invite) {
    return (
      <CenteredState>
        <div className="max-w-md text-center">
          <h1 className="font-['Outfit'] font-bold text-white text-2xl md:text-3xl mb-3">
            We couldn't open this invite
          </h1>
          <p className="text-white/75 text-sm leading-relaxed mb-6">{error}</p>
          <button
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[var(--lime)] text-[var(--charcoal)] font-semibold text-sm"
          >
            <ArrowLeft size={14} /> Back to MediTalk
          </button>
        </div>
      </CenteredState>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--charcoal)] grain-dark relative flex items-center justify-center p-6 overflow-hidden">
      <div className="absolute -top-32 -left-24 w-[420px] h-[420px] rounded-full bg-[var(--lime)]/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -right-24 w-[420px] h-[420px] rounded-full bg-[var(--teal)]/15 blur-3xl pointer-events-none" />

      <div className="w-full max-w-2xl relative z-10">
        <div className="text-center mb-8">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-[var(--lime)] mb-3">
            <ShieldCheck size={14} /> Admin invite
          </span>
          <h1 className="font-['Outfit'] font-bold text-white tracking-tight text-3xl md:text-4xl mb-2 leading-[1.05]">
            Welcome to the MediTalk admin.
          </h1>
          <p className="text-white/75 text-sm">
            You're joining as <strong className="text-white">{invite.email}</strong> (role: {invite.role}). Two quick steps.
          </p>
        </div>

        <form onSubmit={submit} className="bg-white rounded-2xl border border-[var(--light-border)] shadow-2xl p-6 md:p-8 grid md:grid-cols-2 gap-6">
          {/* Step 1: TOTP scan */}
          <div className="space-y-4">
            <div>
              <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-semibold text-[var(--text-muted)] mb-1">
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[var(--lime)] text-[var(--charcoal)] text-[10px] font-bold">1</span>
                Scan with your authenticator
              </span>
              <p className="text-[13px] text-[var(--text-muted)] leading-relaxed mt-2">
                Open Google Authenticator, 1Password, Microsoft Authenticator or any TOTP app, tap <strong>Add</strong> and scan this QR.
              </p>
            </div>
            <div className="rounded-2xl border border-[var(--light-border)] bg-[var(--bone-soft)] p-4 flex flex-col items-center">
              <img
                src={`data:image/png;base64,${invite.totp_qr_base64}`}
                alt="Scan this QR code with your authenticator app"
                className="w-44 h-44 rounded-lg bg-white"
                data-testid="admin-invite-qr"
              />
              <p className="mt-3 text-[10.5px] text-[var(--text-muted)] text-center font-mono break-all max-w-full">
                If you can't scan, enter this manually:<br />
                <span className="text-[var(--charcoal)] tracking-widest">{invite.totp_secret}</span>
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--charcoal)] mb-1.5">
                Code from your app
              </label>
              <div className="relative">
                <KeyRound size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={8}
                  value={totp}
                  onChange={(e) => setTotp(e.target.value.replace(/[^0-9 ]/g, ""))}
                  className="w-full h-11 pl-10 pr-3 rounded-lg border border-[var(--light-border)] bg-white text-[var(--charcoal)] text-[15px] font-mono tracking-[0.35em] focus:outline-none focus:border-[var(--teal)] focus:ring-2 focus:ring-[var(--teal)]/20"
                  placeholder="123 456"
                  required
                  autoComplete="one-time-code"
                  data-testid="admin-invite-totp"
                />
              </div>
            </div>
          </div>

          {/* Step 2: Set password */}
          <div className="space-y-4">
            <div>
              <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-semibold text-[var(--text-muted)] mb-1">
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[var(--lime)] text-[var(--charcoal)] text-[10px] font-bold">2</span>
                Pick a strong password
              </span>
              <p className="text-[13px] text-[var(--text-muted)] leading-relaxed mt-2">
                At least 12 characters, one upper, one lower, one digit, one symbol. Use a password manager , don't make one up.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--charcoal)] mb-1.5">
                Password
              </label>
              <div className="relative">
                <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full h-11 pl-10 pr-3 rounded-lg border border-[var(--light-border)] bg-white text-[var(--charcoal)] focus:outline-none focus:border-[var(--teal)] focus:ring-2 focus:ring-[var(--teal)]/20"
                  required
                  autoComplete="new-password"
                  data-testid="admin-invite-password"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--charcoal)] mb-1.5">
                Confirm password
              </label>
              <div className="relative">
                <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                <input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="w-full h-11 pl-10 pr-3 rounded-lg border border-[var(--light-border)] bg-white text-[var(--charcoal)] focus:outline-none focus:border-[var(--teal)] focus:ring-2 focus:ring-[var(--teal)]/20"
                  required
                  autoComplete="new-password"
                  data-testid="admin-invite-password-confirm"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting || password.length < 12 || password !== confirm || totp.replace(/\s+/g, "").length < 6}
              className="btn-teal w-full disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="admin-invite-accept-btn"
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Setting up…
                </>
              ) : (
                <>
                  <Check size={16} />
                  Activate my account
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const CenteredState = ({ children }) => (
  <div className="min-h-screen bg-[var(--charcoal)] grain-dark flex items-center justify-center p-6">
    {children}
  </div>
);
